/**
 * Configuration definitions for ERC environment variables
 * This file is the single source of truth for all environment variable configurations
 */

export type ConfigGroup = 'features' | 'execution' | 'experimental'

export type ConfigSubsection = 
  | 'Post-Evaluation Interventions'
  | 'World-in-the-Loop' 
  | 'Qualitative Judge' 
  | 'Mutual Predictability'
  | 'Performance Comparison Analyzer'
  | 'Debug'
  | 'Belief Measurement'
  | 'Performance'
  | 'Reuse and Recompute'
  | 'API Keys'

export type CommandTarget = string[] // Array of 'reasoning', 'analyzers', 'trainers'

export interface ConfigDefinition {
  key: string
  group: ConfigGroup
  subsection?: ConfigSubsection
  mandatory: boolean
  description: string
  type: 'string' | 'number' | 'boolean' | 'select' | 'multiselect'
  options?: string[] // For select/multiselect types
  defaultValue?: string | number | boolean | ((state: ConfigState) => string | number | boolean | undefined)
  editable?: boolean // Default true, set false for features from IndexPage
  isSupported?: (state: ConfigState) => boolean // Whether this config is supported given current state
  commandTarget?: CommandTarget // Which command(s) this env var applies to (default: ['reasoning'])
}

export interface ConfigState {
  [key: string]: string | number | boolean | string[] | undefined
}

/**
 * Get default value for a configuration key given the current state
 */
export function getDefaultValue(key: string, state: ConfigState): string | number | boolean | undefined {
  const def = CONFIG_DEFINITIONS.find(d => d.key === key)
  if (!def) return undefined
  
  if (typeof def.defaultValue === 'function') {
    return def.defaultValue(state)
  }
  // Explicitly return undefined if defaultValue is undefined
  // This allows configs to have no default value
  return def.defaultValue
}

/**
 * Check if a configuration is supported given the current state
 */
export function isConfigSupported(key: string, state: ConfigState): boolean {
  const def = CONFIG_DEFINITIONS.find(d => d.key === key)
  if (!def) return false
  
  if (def.isSupported) {
    return def.isSupported(state)
  }
  return true // Default to supported if no function specified
}

/**
 * Helper functions for checking algorithm presence
 */
const hasAlgorithm = (state: ConfigState, algo: string): boolean => {
  const algos = state.ALGO_NAMES
  return Array.isArray(algos) && algos.includes(algo)
}

const hasAnalyzer = (state: ConfigState, analyzer: string): boolean => {
  const analyzers = state.ANALYZERS
  // Check if 'all' is selected or the specific analyzer is selected
  return Array.isArray(analyzers) && (analyzers.includes('all') || analyzers.includes(analyzer))
}

/**
 * Main configuration definitions
 */
export const CONFIG_DEFINITIONS: ConfigDefinition[] = [
  // ========== FEATURES (from IndexPage selection) ==========
  {
    key: 'ALGO_NAMES',
    group: 'features',
    mandatory: true,
    description: 'Evaluation strategy(-ies) to use',
    type: 'multiselect',
    options: ['GroundTruthAccuracy', 'MartingaleStrategy', 'WorldInTheLoop', 'QualitativeJudge', 'MutualPredictStrategy', 'GraderWrapper'],
    editable: false,
    commandTarget: ['reasoning', 'analyzers'],
  },
  {
    key: 'DOMAIN_NAMES',
    group: 'features',
    mandatory: true,
    description: 'Domain name(s) to evaluate',
    type: 'multiselect',
    options: ['Forecasting', 'OpenReview', 'CMVBinary', 'CMVFreeForm', 'Research', 'ForumComment'],
    editable: false,
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'REASONING_MODE_NAMES',
    group: 'features',
    mandatory: true,
    description: 'Reasoning mode name(s)',
    type: 'multiselect',
    options: ['DirectInference', 'ChainOfThought', 'SelfDebate'],
    editable: false,
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'POLICY_LIST',
    group: 'features',
    mandatory: false,
    description: 'Policies to evaluate (comma-separated)',
    type: 'string',
    editable: true, // Allow editing even after filling defaults
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'SYSTEM_PROMPTS',
    group: 'features',
    mandatory: false,
    description: 'System prompts to test (comma-separated)',
    type: 'string',
    editable: false,
    commandTarget: ['reasoning'],
  },
  {
    key: 'ANALYZERS',
    group: 'features',
    mandatory: false,
    description: 'Result analyzers to run',
    type: 'multiselect',
    options: ['all', 'PerformanceComparisonAnalyzer', 'EvaluationRelationshipAnalyzer', 'CausalAttributionAnalyzer', 'CrossSetupAgreementAnalyzer', 'TokenLevelEvidenceAnalyzer', 'ResultsCatalogAnalyzer', 'TrajectoryScoreAnalyzer'],
    editable: false,
    defaultValue: 'all',
    commandTarget: ['analyzers'],
  },

  // ========== EXECUTION ==========
  {
    key: 'NUM_TRAJECTORIES',
    group: 'execution',
    mandatory: false,
    description: 'Number of trajectories to generate per combination (default: differs by algorithm and domain)',
    type: 'number',
    defaultValue: undefined,
    commandTarget: ['reasoning'],
  },
  {
    key: 'DEFAULT_SPLIT',
    group: 'execution',
    mandatory: false,
    description: 'Default dataset split to sample from. Use train if the resulting evaluation data may be used for training later.',
    type: 'select',
    options: ['train', 'test'],
    defaultValue: 'train',
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'DIR_NAME',
    group: 'execution',
    mandatory: false,
    description: 'Directory name for rollout output, analysis input, and training data. Use "/" to indicate subdirectory (path relative to `data/runs`). For analysis/training, it can be a comma-separated list of patterns, e.g. "WITL*,QJ*/rbr*". Use "[TIMESTAMP]" to indicate insertion of timestamp. When ALGO_NAMES is a single algorithm, use "[XX_CONFIG]" to indicate insertion of experimental config summary string.',
    type: 'string',
    defaultValue: (state) => {
      const algo_names = Array.isArray(state.ALGO_NAMES) ? state.ALGO_NAMES.join('-') : state.ALGO_NAMES
      const domain_names = Array.isArray(state.DOMAIN_NAMES) ? state.DOMAIN_NAMES.join('-') : state.DOMAIN_NAMES
      const reasoning_mode_names = Array.isArray(state.REASONING_MODE_NAMES) ? state.REASONING_MODE_NAMES.join('-') : state.REASONING_MODE_NAMES

      let setup_name = `${algo_names}.${domain_names}.${reasoning_mode_names}`
      // remove all lower-case letters
      setup_name = setup_name.replace(/[a-z]/g, '')

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
      let config_dir = new Array<string>()
      if (Array.isArray(state.ALGO_NAMES) && state.ALGO_NAMES.length == 1) {
        if (state.ALGO_NAMES[0] == 'QualitativeJudge') {
          config_dir.push(`[QJ_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'MutualPredictStrategy') {
          config_dir.push(`[MP_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'MartingaleStrategy') {
          config_dir.push(`[MS_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'GroundTruthAccuracy') {
          config_dir.push(`[GTA_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'SycophanticReasoning') {
          config_dir.push(`[SR_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'WorldInTheLoop') {
          config_dir.push(`[WITL_CONFIG]`)
        }
        else if (state.ALGO_NAMES[0] == 'GraderWrapper') {
          config_dir.push(`[GW_CONFIG]`)
        }
      }
      if (config_dir.length == 0) {
        config_dir.push(timestamp)
      }

      return `${setup_name}/${config_dir.join('=')}`
    },
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'TRAINED_POLICY_NAME_PATTERN',
    group: 'execution',
    mandatory: false,
    description: 'Pattern for naming trained policies. Use "[DIR_NAME]" to indicate insertion of DIR_NAME, "[NUM_TRAIN_SAMPLES]" to indicate insertion of number of training samples, and "[NUM_VAL_SAMPLES]" likewise. Will be prepended with the base policy name and appended with timestamp and hash.',
    type: 'string',
    defaultValue: '[DIR_NAME]-N=[NUM_TRAIN_SAMPLES]',
    commandTarget: ['trainers'],
  },
  {
    key: 'USE_RAY',
    group: 'execution',
    mandatory: false,
    description: 'Use Ray for distributed processing of API calls (recommended for performance)',
    subsection: 'Performance',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'USE_OPENROUTER',
    group: 'execution',
    mandatory: false,
    description: 'Use OpenRouter for model routing (requires USE_RAY=true)',
    subsection: 'Performance',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'USE_BATCH',
    group: 'execution',
    mandatory: false,
    description: 'Use provider-specific batch APIs (requires USE_RAY=false)',
    subsection: 'Performance',
    type: 'boolean',
    defaultValue: false,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'PARALLEL_BATCH',
    group: 'execution',
    mandatory: false,
    description: 'Use async parallelism across runs',
    subsection: 'Performance',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'MAX_WORKERS',
    group: 'execution',
    mandatory: false,
    description: 'Maximum number of Ray workers',
    subsection: 'Performance',
    type: 'number',
    defaultValue: undefined,
    commandTarget: ['reasoning'],
  },
  {
    key: 'LOCALMODEL_MAX_CONCURRENT',
    group: 'execution',
    mandatory: false,
    description: 'Maximum number of concurrent LocalModel training + inference runs (default: 1)',
    subsection: 'Performance',
    type: 'number',
    defaultValue: 1,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'RERUN_INCOMPLETE',
    group: 'execution',
    mandatory: false,
    description: 'Rerun experiments that contain < NUM_TRAJECTORIES trajectories',
    subsection: 'Reuse and Recompute',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'RECOMPUTE_RESULTS',
    group: 'execution',
    mandatory: false,
    description: 'If to recompute and overwrite existing final result JSON file',
    subsection: 'Reuse and Recompute',
    type: 'boolean',
    defaultValue: false,
    commandTarget: ['reasoning'],
  },
  {
    key: 'RECOMPUTE_TRAJECTORIES',
    group: 'execution',
    mandatory: false,
    description: 'When to recompute trajectories JSON file',
    subsection: 'Reuse and Recompute',
    type: 'select',
    options: ['never', 'missing', 'always'],
    defaultValue: 'missing',
    commandTarget: ['reasoning'],
  },
  {
    key: 'RECOMPUTE_BELIEFS',
    group: 'execution',
    mandatory: false,
    description: 'When to recompute beliefs JSON file',
    subsection: 'Reuse and Recompute',
    type: 'select',
    options: ['never', 'missing', 'always'],
    defaultValue: 'missing',
    commandTarget: ['reasoning'],
  },
  {
    key: 'DEBUG',
    group: 'execution',
    mandatory: false,
    description: 'Debug level (0=none, 1=basic, 2=verbose)',
    subsection: 'Debug',
    type: 'select',
    options: ['0', '1', '2'],
    defaultValue: '0',
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'SHOW_PROGRESS',
    group: 'execution',
    mandatory: false,
    description: 'Show progress bars',
    subsection: 'Debug',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'NO_RETRY',
    group: 'execution',
    subsection: 'Debug',
    mandatory: false,
    description: 'Disable retry mechanism for API calls',
    type: 'boolean',
    defaultValue: false,
    commandTarget: ['reasoning', 'trainers'],
  },

  // ========== API KEYS ==========
  {
    key: 'OPENROUTER_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'OpenRouter API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'HUGGINGFACE_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'HuggingFace API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'TOGETHER_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'TogetherAI API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'OPENAI_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'OpenAI API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'ANTHROPIC_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'Anthropic API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'GOOGLE_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'Google API key',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'WANDB_API_KEY',
    group: 'execution',
    subsection: 'API Keys',
    mandatory: false,
    description: 'Weights & Biases API key for training metrics logging',
    type: 'string',
    defaultValue: undefined,
    commandTarget: ['trainers'],
  },

  // ========== EXPERIMENTAL ==========
  {
    key: 'JUDGE_POLICY_NAMES',
    group: 'experimental',
    mandatory: false,
    description: 'Judge policy names (comma-separated)',
    type: 'string',
    defaultValue: (state) => {
      const judge_policies = [] as string[] 
      if (hasAlgorithm(state, 'QualitativeJudge')) {
        judge_policies.push('gemini-2.5-pro')
      }
      if (hasAlgorithm(state, 'MartingaleStrategy') || hasAlgorithm(state, 'GroundTruthAccuracy') || hasAlgorithm(state, 'SycophanticReasoning')) {
        judge_policies.push('gpt-4o')
      }
      if (hasAlgorithm(state, 'WorldInTheLoop') || hasAlgorithm(state, 'MutualPredictStrategy')) {
        judge_policies.push('Qwen/Qwen3-0.6B-Base')
      }
      return judge_policies.join(',')
    },
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'TEMPERATURE',
    group: 'experimental',
    mandatory: false,
    description: 'Model temperature for non-Ray API models',
    type: 'number',
    defaultValue: 0.25,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'PRESENCE_PENALTY',
    group: 'experimental',
    mandatory: false,
    description: 'Presence penalty for non-Ray API models',
    type: 'number',
    defaultValue: 0.0,
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'POLICY_LIST_MODE',
    group: 'experimental',
    mandatory: false,
    description: 'Canonical policy list, overrides POLICY_LIST',
    type: 'select',
    options: ['frontier', 'legacy', 'neurips', 'trained-api', 'trained-local'],
    defaultValue: undefined,
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  {
    key: 'FORBIDDEN_MODELS',
    group: 'experimental',
    mandatory: false,
    description: 'Remove policies whose names contain any of the following (comma-separated)',
    type: 'string',
    defaultValue: undefined, // No default - won't appear in command unless set
    commandTarget: ['reasoning', 'analyzers', 'trainers'],
  },
  // ========== EXPERIMENTAL - Belief Measurement ==========
  {
    key: 'DISABLE_SYSTEM_PROMPT_IN_BELIEF_MEASUREMENT',
    group: 'experimental',
    subsection: 'Belief Measurement',
    mandatory: false,
    description: 'Disable system prompts for judge policies during belief measurement',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'USE_FIXED_JUDGE',
    group: 'experimental',
    subsection: 'Belief Measurement',
    mandatory: false,
    description: 'Use fixed judge for evaluation instead of the evaluated policy itself',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'OBJECTIVE_BELIEF',
    group: 'experimental',
    subsection: 'Belief Measurement',
    mandatory: false,
    description: 'Judge estimates beliefs from the standpoint of the evaluated policy',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'USE_PER_TRAJ_BELIEF_MEASURE',
    group: 'experimental',
    subsection: 'Belief Measurement',
    mandatory: false,
    description: 'Use per-trajectory belief measurement instead of per-step',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },
  {
    key: 'DECOUPLE_TRAJECTORY_BELIEFS',
    group: 'experimental',
    subsection: 'Belief Measurement',
    mandatory: false,
    description: 'Save belief measurement and trajectories in separate files',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['reasoning'],
  },

  // ========== EXPERIMENTAL - World-in-the-Loop ==========
  {
    key: 'WITL_RECOMPUTE_POLICY',
    group: 'experimental',
    subsection: 'World-in-the-Loop',
    mandatory: false,
    description: 'Which World-in-the-Loop components to recompute',
    type: 'select',
    options: ['investigation', 'uplift', 'upliftblanket', 'all'],
    defaultValue: 'all',
    isSupported: (state) => hasAlgorithm(state, 'WorldInTheLoop'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'WITL_FORECASTER_TEMPLATE',
    group: 'experimental',
    subsection: 'World-in-the-Loop',
    mandatory: false,
    description: 'World-in-the-Loop forecaster template',
    type: 'select',
    options: ['vanilla', 'rephrase', 'toolcall'],
    defaultValue: 'vanilla',
    isSupported: (state) => hasAlgorithm(state, 'WorldInTheLoop'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'WITL_PER_TOKEN_UPLIFT',
    group: 'experimental',
    subsection: 'World-in-the-Loop',
    mandatory: false,
    description: 'Use per-token uplift calculation',
    type: 'boolean',
    defaultValue: true,
    isSupported: (state) => hasAlgorithm(state, 'WorldInTheLoop'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'WITL_FORECASTER',
    group: 'experimental',
    subsection: 'World-in-the-Loop',
    mandatory: false,
    description: 'World-in-the-Loop forecaster policy name',
    type: 'string',
    defaultValue: undefined,
    isSupported: (state) => hasAlgorithm(state, 'WorldInTheLoop'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'WITL_REPHRASER',
    group: 'experimental',
    subsection: 'World-in-the-Loop',
    mandatory: false,
    description: 'World-in-the-Loop rephraser policy name (only used if WITL_FORECASTER_TEMPLATE is set to "rephrase")',
    type: 'string',
    defaultValue: undefined,
    isSupported: (state) => hasAlgorithm(state, 'WorldInTheLoop'),
    commandTarget: ['reasoning'],
  },

  // ========== EXPERIMENTAL - Qualitative Judge ==========
  {
    key: 'QUALITATIVE_JUDGE_USE_FEW_SHOT',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Use few-shot examples in qualitative judge',
    type: 'boolean',
    defaultValue: true,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'QUAL_RED_TEAM_MODE',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Qualitative judge red team mode',
    type: 'select',
    options: ['none', 'red', 'red_blue', 'red_blue_resolution'],
    defaultValue: 'red_blue_resolution',
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'QUAL_FEW_SHOT_PERMUTE',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Permute few-shot examples',
    type: 'boolean',
    defaultValue: false,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'QUAL_EXAMPLE_RECOMPUTATION_ROUNDS',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Example recomputation rounds',
    type: 'number',
    defaultValue: 1,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'QUAL_FEW_SHOT_BOOTSTRAP_ROUNDS',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Few-shot bootstrap rounds',
    type: 'number',
    defaultValue: 0,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'QUAL_BOOTSTRAP_RESPECT_PERMUTE',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Respect permutation in bootstrap',
    type: 'boolean',
    defaultValue: false,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'FEW_SHOT_SOURCE_PATH',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Few-shot source path',
    type: 'string',
    defaultValue: 'data/questions/conceptual_human_examples.json',
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'FEW_SHOT_BOOTSTRAP_SOURCE_PATH',
    group: 'experimental',
    subsection: 'Qualitative Judge',
    mandatory: false,
    description: 'Few-shot bootstrap source path',
    type: 'string',
    defaultValue: undefined,
    isSupported: (state) => hasAlgorithm(state, 'QualitativeJudge'),
    commandTarget: ['reasoning'],
  },

  // ========== EXPERIMENTAL - Mutual Predictability ==========
  {
    key: 'MP_PREDICTOR_CHOICE',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'MutualPredictStrategy predictor policy choice',
    type: 'select',
    options: ['evaluated', 'random_non_evaluated', 'random_any'],
    defaultValue: 'random_non_evaluated',
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_PREDICTED_CHOICE',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'MutualPredictStrategy predicted policy choice',
    type: 'select',
    options: ['evaluated', 'random_non_evaluated', 'random_any'],
    defaultValue: 'evaluated',
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_TARGET_QUESTION',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'MutualPredictStrategy target question choice',
    type: 'select',
    options: ['evaluated_question', 'random_non_evaluated_question', 'random_any_question'],
    defaultValue: 'evaluated_question',
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_CONTEXT_QUESTIONS',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'MutualPredictStrategy context question choice',
    type: 'select',
    options: ['evaluated_question', 'k_random_non_evaluated'],
    defaultValue: 'k_random_non_evaluated',
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_K_CONTEXT',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'Number of context questions',
    type: 'number',
    defaultValue: 3,
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_TRIALS_PER_SAMPLE',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'Trials per sample to average',
    type: 'number',
    defaultValue: 3,
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_SCORING',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'MutualPredictStrategy scoring method',
    type: 'select',
    options: ['uplift', 'conditional_only', 'judge_consistency'],
    defaultValue: 'uplift',
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },
  {
    key: 'MP_PREDICTEE_BEHAVIOR_EXAMPLES',
    group: 'experimental',
    subsection: 'Mutual Predictability',
    mandatory: false,
    description: 'Number of predictee behavioral examples',
    type: 'number',
    defaultValue: 0,
    isSupported: (state) => hasAlgorithm(state, 'MutualPredictStrategy'),
    commandTarget: ['reasoning'],
  },

  // ========== EXPERIMENTAL - Post-Evaluation Interventions ==========
  {
    key: 'FEWSHOT_USING_EVAL_RESULTS',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Use eval results to improve policies with few-shot prompting on positive examples. Requires TrajectoryScoreAnalyzer to be enabled.',
    type: 'boolean',
    defaultValue: true,
    commandTarget: ['trainers'],
  },
  {
    key: 'SFT_USING_EVAL_RESULTS',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Use eval results to finetune policies on positive examples. Requires TrajectoryScoreAnalyzer to be enabled.',
    type: 'boolean',
    defaultValue: false,
    commandTarget: ['trainers'],
  },
  {
    key: 'RL_USING_EVAL_RESULTS',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Use eval results to train policies with reinforcement learning. Requires TrajectoryScoreAnalyzer to be enabled.',
    type: 'boolean',
    defaultValue: false,
    commandTarget: ['trainers'],
  },
  {
    key: 'VALIDATION_STRATEGY',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Validation strategy for training (none, train, gt)',
    type: 'select',
    options: ['none', 'train', 'gt'],
    defaultValue: 'none',
    commandTarget: ['trainers'],
  },
  {
    key: 'SFT_TOP_PERCENTAGE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Percentage of top-scoring trajectories to use for SFT',
    type: 'number',
    defaultValue: 0.1,
    commandTarget: ['trainers'],
  },
  {
    key: 'SFT_LEARNING_RATE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Learning rate for SFT',
    type: 'number',
    defaultValue: 1e-5,
    commandTarget: ['trainers'],
  },
  {
    key: 'SFT_NUM_EPOCHS',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Number of epochs for SFT',
    type: 'number',
    defaultValue: 2,
    commandTarget: ['trainers'],
  },
  {
    key: 'SFT_BATCH_SIZE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Batch size for SFT',
    type: 'number',
    defaultValue: 4,
    commandTarget: ['trainers'],
  },
  {
    key: 'FEWSHOT_TOP_COUNT',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Number of top-scoring trajectories to use for few-shot prompting',
    type: 'number',
    defaultValue: 100,
    commandTarget: ['trainers'],
  },
  {
    key: 'FEWSHOT_TOP_PERCENTAGE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Percentage of top-scoring trajectories to use for few-shot prompting',
    type: 'number',
    defaultValue: 0.1,
    commandTarget: ['trainers'],
  },
  {
    key: 'RL_NUM_SAMPLES',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Number of samples for RL training',
    type: 'number',
    defaultValue: 100,
    commandTarget: ['trainers'],
  },
  {
    key: 'RL_NUM_EPOCHS',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Number of training epochs for RL',
    type: 'number',
    defaultValue: 2,
    commandTarget: ['trainers'],
  },
  {
    key: 'RL_LEARNING_RATE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Learning rate for RL training',
    type: 'number',
    defaultValue: 0.00001,
    commandTarget: ['trainers'],
  },
  {
    key: 'GRADER_SPEC',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Grader specification for GraderWrapper (JSON string or env var name)',
    type: 'string',
    defaultValue: 'PythonBrierGrader',
    commandTarget: ['reasoning', 'trainers'],
  },
  {
    key: 'GRADER_TYPE',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Grader type for RL training',
    type: 'select',
    options: ['model_brier', 'python_brier', 'model_agreement'],
    defaultValue: 'model_brier',
    commandTarget: ['trainers', 'reasoning'],
  },
  {
    key: 'GRADER_MODEL',
    group: 'experimental',
    subsection: 'Post-Evaluation Interventions',
    mandatory: false,
    description: 'Optional: Model for grader in GraderWrapper',
    type: 'string',
    defaultValue: "o4-mini",
    commandTarget: ['reasoning', 'trainers'],
  },

  // ========== ANALYZER - Performance Comparison ==========
  {
    key: 'REMOVE_N_OUTGROUP_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'Number of outgroup data points (with most different setup names) to remove from per-setup performance comparison',
    type: 'number',
    defaultValue: 0,
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'REMOVE_N_OUTGROUP_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'Number of outgroup data points (with most different setup names) to remove from across-setups performance comparison',
    type: 'number',
    defaultValue: 0,
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'GROUP_MODE_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to group data points in the per-setup performance comparison ("none", "qwen", "substr_KEY1=VALUE1_...")',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'GROUP_MODE_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to group data points in the across-setups performance comparison ("none", "qwen", "substr_KEY1=VALUE1_...")',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ASSIGN_X_COORDS_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to assign x coordinates when plotting per-setup performance comparison',
    type: 'select',
    options: ['none', 'lexical', 'lexical_and_group', 'size', 'size_and_group', 'size_group_and_random'],
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ASSIGN_X_COORDS_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to assign x coordinates when plotting across-setups performance comparison',
    type: 'select',
    options: ['none', 'lexical', 'lexical_and_group', 'size', 'size_and_group', 'size_group_and_random'],
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_SHOWS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'Whether the performance comparison plot shows Arena Elo scores alongside evaluation loss',
    type: 'boolean',
    defaultValue: true,
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_X_TICKS_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to assign x ticks when plotting per-setup performance comparison ("names" or list of string labels)',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_X_TICKS_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to assign x ticks when plotting across-setups performance comparison ("names" or list of string labels)',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_X_LABEL_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'X-axis label for per-setup performance comparison',
    type: 'string',
    defaultValue: 'Setup Index',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_X_LABEL_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'X-axis label for across-setups performance comparison',
    type: 'string',
    defaultValue: 'Setup Index',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_Y_COORDS_SETUP',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to transform y coordinates in the per-setup performance comparison ("none", "negate", "log", comma-separated list of python math functions to apply)',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
  {
    key: 'ELO_CORR_Y_COORDS_ACROSS_SETUPS',
    group: 'experimental',
    subsection: 'Performance Comparison Analyzer',
    mandatory: false,
    description: 'How to transform y coordinates in the across-setups performance comparison ("none", "negate", "log", comma-separated list of python math functions to apply)',
    type: 'string',
    defaultValue: 'none',
    isSupported: (state) => hasAnalyzer(state, 'PerformanceComparisonAnalyzer'),
    commandTarget: ['analyzers'],
  },
]

/**
 * Generate markdown documentation for all environment variables
 */
export function generateMarkdownDocs(): string {
  const lines: string[] = []
  
  // Group configs by their groups and subsections
  const grouped: Record<string, Record<string, ConfigDefinition[]>> = {
    features: {},
    execution: {},
    experimental: {}
  }
  
  for (const def of CONFIG_DEFINITIONS) {
    const subsection = def.subsection || 'default'
    if (!grouped[def.group]) grouped[def.group] = {}
    if (!grouped[def.group][subsection]) grouped[def.group][subsection] = []
    grouped[def.group][subsection].push(def)
  }
  
  // Generate documentation
  lines.push('## Environment Variables')
  lines.push('')
  lines.push('This document lists all environment variables supported by the ERC evaluation system.')
  lines.push('')
  
  // Features section
  if (Object.keys(grouped.features).length > 0) {
    lines.push('### Features')
    lines.push('')
    lines.push('These variables control which features to evaluate. They are typically set from the UI selection.')
    lines.push('')
    
    for (const def of grouped.features.default || []) {
      lines.push(`- **${def.key}**: ${def.description}`)
      if (def.options) {
        lines.push(`  - Options: ${def.options.join(', ')}`)
      }
      if (def.mandatory) {
        lines.push('  - Required: Yes')
      }
      if (def.commandTarget && (def.commandTarget.length !== 1 || def.commandTarget[0] !== 'reasoning')) {
        lines.push(`  - Used by: ${def.commandTarget.join(', ')}`)
      }
      lines.push('')
    }
  }
  
  // Execution section
  if (Object.keys(grouped.execution).length > 0) {
    lines.push('### Execution')
    lines.push('')
    lines.push('These variables control how the evaluation runs are executed.')
    lines.push('')
    
    // Default execution configs
    for (const def of grouped.execution.default || []) {
      lines.push(`- **${def.key}**: ${def.description}`)
      if (def.type === 'select' && def.options) {
        lines.push(`  - Options: ${def.options.join(', ')}`)
      }
      if (def.type === 'boolean') {
        lines.push('  - Type: Boolean (0 or 1)')
      }
      if (def.type === 'number') {
        lines.push('  - Type: Number')
      }
      if (typeof def.defaultValue !== 'undefined' && typeof def.defaultValue !== 'function') {
        lines.push(`  - Default: ${def.defaultValue}`)
      }
      if (def.commandTarget && (def.commandTarget.length !== 1 || def.commandTarget[0] !== 'reasoning')) {
        lines.push(`  - Used by: ${def.commandTarget.join(', ')}`)
      }
      lines.push('')
    }
    
    // Subsections within execution
    for (const [subsection, configs] of Object.entries(grouped.execution)) {
      if (subsection === 'default' || !configs || configs.length === 0) continue
      
      lines.push(`#### ${subsection}`)
      lines.push('')
      
      for (const def of configs) {
        lines.push(`- **${def.key}**: ${def.description}`)
        if (def.type === 'select' && def.options) {
          lines.push(`  - Options: ${def.options.join(', ')}`)
        }
        if (def.type === 'boolean') {
          lines.push('  - Type: Boolean (0 or 1)')
        }
        if (def.type === 'number') {
          lines.push('  - Type: Number')
        }
        if (typeof def.defaultValue !== 'undefined' && typeof def.defaultValue !== 'function') {
          lines.push(`  - Default: ${def.defaultValue}`)
        }
        if (def.commandTarget && (def.commandTarget.length !== 1 || def.commandTarget[0] !== 'reasoning')) {
          lines.push(`  - Used by: ${def.commandTarget.join(', ')}`)
        }
        lines.push('')
      }
    }
  }
  
  // Experimental section
  if (Object.keys(grouped.experimental).length > 0) {
    lines.push('### Experimental')
    lines.push('')
    lines.push('These variables control experimental features and algorithm-specific behaviors.')
    lines.push('')
    
    // Default experimental configs
    for (const def of grouped.experimental.default || []) {
      lines.push(`- **${def.key}**: ${def.description}`)
      if (def.type === 'select' && def.options) {
        lines.push(`  - Options: ${def.options.join(', ')}`)
      }
      if (def.type === 'boolean') {
        lines.push('  - Type: Boolean (0 or 1)')
      }
      if (def.type === 'number') {
        lines.push('  - Type: Number')
      }
      if (typeof def.defaultValue !== 'undefined' && typeof def.defaultValue !== 'function') {
        lines.push(`  - Default: ${def.defaultValue}`)
      }
      if (def.commandTarget && (def.commandTarget.length !== 1 || def.commandTarget[0] !== 'reasoning')) {
        lines.push(`  - Used by: ${def.commandTarget.join(', ')}`)
      }
      lines.push('')
    }
    
    // Subsections within experimental
    for (const [subsection, configs] of Object.entries(grouped.experimental)) {
      if (subsection === 'default' || !configs || configs.length === 0) continue
      
      lines.push(`#### ${subsection}`)
      lines.push('')
      
      for (const def of configs) {
        lines.push(`- **${def.key}**: ${def.description}`)
        if (def.type === 'select' && def.options) {
          lines.push(`  - Options: ${def.options.join(', ')}`)
        }
        if (def.type === 'boolean') {
          lines.push('  - Type: Boolean (0 or 1)')
        }
        if (def.type === 'number') {
          lines.push('  - Type: Number')
        }
        if (typeof def.defaultValue !== 'undefined' && typeof def.defaultValue !== 'function') {
          lines.push(`  - Default: ${def.defaultValue}`)
        }
        if (def.commandTarget && (def.commandTarget.length !== 1 || def.commandTarget[0] !== 'reasoning')) {
          lines.push(`  - Used by: ${def.commandTarget.join(', ')}`)
        }
        lines.push('')
      }
    }
  }
  
  return lines.join('\n')
}

/**
 * Helper function to convert config state to environment variables format
 */
export function configToEnvVars(config: ConfigState, targets: CommandTarget = ['reasoning', 'analyzers', 'trainers']): Record<string, string> {
  const envVars: Record<string, string> = {}

  for (const [key, value] of Object.entries(config)) {
    // Skip undefined, null, empty string, and empty arrays
    if (value === undefined || value === null || value === '' ||
        (Array.isArray(value) && value.length === 0)) continue

    // Check if this config is supported in the current state
    if (!isConfigSupported(key, config)) continue

    // Check if this config applies to the target command
    const def = CONFIG_DEFINITIONS.find(d => d.key === key)
    if (!def) continue

    const commandTarget = def.commandTarget || ['reasoning']
    const targetArray = Array.isArray(commandTarget) ? commandTarget : [commandTarget]
    const targetsArray = Array.isArray(targets) ? targets : [targets]

    // Check if any of the requested targets match the definition's targets
    if (!targetsArray.some(t => targetArray.includes(t))) {
      continue // Skip this env var
    }
    
    if (Array.isArray(value)) {
      envVars[key] = value.join(',')
    } else if (typeof value === 'boolean') {
      envVars[key] = value ? '1' : '0'
    } else {
      envVars[key] = String(value)
    }
  }
  
  return envVars
}

/**
 * Generate command preview from config
 */
export function generateCommandPreview(
  config: ConfigState,
  selectedPhases: string[] = ['reasoning', 'analyzers']
): string {
  const phaseCommands: Record<string, { script: string, label: string }> = {
    'reasoning': { script: 'scripts.run_reasoning', label: 'Run reasoning evaluation' },
    'analyzers': { script: 'scripts.run_analyzers', label: 'Run analyzers' },
    'trainers': { script: 'scripts.run_trainers', label: 'Run training' }
  }

  const commands: string[] = []

  for (const phase of selectedPhases) {
    const phaseEnvVars = configToEnvVars(config, [phase])
    const envStr = Object.entries(phaseEnvVars)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' \\\n  ')

    const phaseInfo = phaseCommands[phase]
    if (phaseInfo && envStr) {
      commands.push(`# ${phaseInfo.label}\n${envStr} \\\n  python -m ${phaseInfo.script}`)
    }
  }

  return commands.join('\n\n') || '# No configuration set'
}

/**
 * Check if a config value is mandatory and missing
 */
export function isMandatoryAndMissing(key: string, config: ConfigState): boolean {
  const def = CONFIG_DEFINITIONS.find(d => d.key === key)
  if (!def || !def.mandatory) return false
  
  // Check if this config is even supported
  if (!isConfigSupported(key, config)) return false
  
  const value = config[key]
  
  // Special case: OPENROUTER_API_KEY is mandatory when USE_OPENROUTER is true
  if (key === 'OPENROUTER_API_KEY') {
    const useOpenRouter = config.USE_OPENROUTER
    if (useOpenRouter === true || useOpenRouter === '1' || useOpenRouter === 1) {
      return !value || value === ''
    }
    return false
  }
  
  return !value || (Array.isArray(value) && value.length === 0) || value === ''
}

/**
 * Get all mandatory fields that are missing
 */
export function getMissingMandatoryFields(config: ConfigState): string[] {
  return CONFIG_DEFINITIONS
    .filter(def => isMandatoryAndMissing(def.key, config))
    .map(def => def.key)
}

/**
 * Get all supported configs for the current state
 */
export function getSupportedConfigs(state: ConfigState): ConfigDefinition[] {
  return CONFIG_DEFINITIONS.filter(def => isConfigSupported(def.key, state))
}

/**
 * Group supported configs by group and subsection
 */
export function groupSupportedConfigs(state: ConfigState): Record<ConfigGroup, Record<string, ConfigDefinition[]>> {
  const supported = getSupportedConfigs(state)
  const grouped: Record<ConfigGroup, Record<string, ConfigDefinition[]>> = {
    features: {},
    execution: {},
    experimental: {},
  }
  
  for (const def of supported) {
    const subsection = def.subsection || 'default'
    if (!grouped[def.group][subsection]) {
      grouped[def.group][subsection] = []
    }
    grouped[def.group][subsection].push(def)
  }
  
  return grouped
}